import { ChoiceRow, HIDDEN_INPUT, PEER_FOCUS_RING, choiceAria, choiceIds } from "./ChoiceRow";
import { CheckIndicator, type IndicatorSize, type IndicatorTone } from "./Indicators";
import { forwardRef, useId, type ComponentPropsWithoutRef, type ReactNode } from "react";

export type CheckboxProps = Omit<ComponentPropsWithoutRef<"input">, "type" | "size" | "onChange" | "checked"> & {
  checked: boolean;
  label?: ReactNode;
  description?: ReactNode;
  onCheckedChange?: (checked: boolean) => void;
  tone?: IndicatorTone;
  size?: IndicatorSize;
};

/** Accessible checkbox: a visually hidden native input with a CheckIndicator, label and description. */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, onCheckedChange, tone = "primary", size = "md", className, checked, disabled, ...rest },
  ref,
) {
  const ids = choiceIds(useId());
  return (
    <ChoiceRow
      size={size}
      disabled={disabled}
      label={label}
      description={description}
      ids={ids}
      className={className}
      input={
        <input
          {...rest}
          ref={ref}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          {...choiceAria(ids, label, description, rest)}
          onChange={(event) => onCheckedChange?.(event.target.checked)}
          className={HIDDEN_INPUT}
        />
      }
      indicator={
        <CheckIndicator checked={checked} tone={tone} size={size} disabled={disabled} className={PEER_FOCUS_RING} />
      }
    />
  );
});
